import { supabase } from '../lib/supabase';
import { getMapIndex, setMapIndex, loadMap, saveMap as saveLocalMap, createMap, deleteMap as deleteLocalMap } from './storage';
import type { MapMeta } from '../types';

const isValidSupabaseMap = (sm: any): boolean =>
  sm &&
  typeof sm.id === 'string' &&
  sm.data &&
  Array.isArray(sm.data.nodes) &&
  Array.isArray(sm.data.connections);

export const pushMap = async (localId: string, userId: string): Promise<void> => {
  console.log(`[Sync] Pushing map ${localId}...`);
  const index = getMapIndex();
  const meta = index.find(m => m.id === localId);
  const data = loadMap(localId);
  if (!meta || !data) {
    console.warn(`[Sync] Map ${localId} not found in storage, skipping push.`);
    return;
  }

  const payload = {
    user_id: userId,
    name: meta.name,
    data,
  };

  try {
    if (meta.supabase_id) {
      const { data: result, error } = await supabase
        .from('maps')
        .update(payload)
        .eq('id', meta.supabase_id)
        .is('deleted_at', null)
        .select('updated_at')
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          console.warn(`[Sync] Map ${localId} (cloud id ${meta.supabase_id}) seems deleted on cloud.`);
          return;
        }
        throw error;
      }

      if (result?.updated_at) {
        console.log(`[Sync] Map ${localId} updated. Server time: ${result.updated_at}`);
        const updatedIndex = getMapIndex().map(m =>
          m.id === localId ? { ...m, updatedAt: result.updated_at } : m
        );
        setMapIndex(updatedIndex);
      }
    } else {
      const { data: result, error } = await supabase
        .from('maps')
        .insert({ ...payload, created_at: meta.createdAt })
        .select('id, updated_at')
        .single();

      if (error) throw error;

      console.log(`[Sync] Map ${localId} created on cloud with id ${result.id}`);
      const updatedIndex = getMapIndex().map(m =>
        m.id === localId ? { ...m, supabase_id: result.id, updatedAt: result.updated_at } : m
      );
      setMapIndex(updatedIndex);
    }
  } catch (err) {
    console.error(`[Sync] Error pushing map ${localId}:`, err);
    throw err;
  }
};

export const deleteMapFromCloud = async (supabaseId: string): Promise<void> => {
  console.log(`[Sync] Soft-deleting map ${supabaseId} on cloud...`);
  const { error } = await supabase
    .from('maps')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', supabaseId);
  if (error) {
    console.error(`[Sync] Error deleting map ${supabaseId} from cloud:`, error);
    throw error;
  }
};

export const pullMaps = async (userId: string): Promise<any[]> => {
  console.log(`[Sync] Pulling maps for user ${userId}...`);
  const { data, error } = await supabase
    .from('maps')
    .select('*')
    .eq('user_id', userId)
    .is('deleted_at', null)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('[Sync] Error pulling maps:', error);
    throw error;
  }
  return data;
};

interface SyncPlan {
  toUpload: MapMeta[];
  toDownload: any[];
  conflicts: { local: any; supabase: any }[];
  toDeleteLocally: MapMeta[];
}

export const buildSyncPlan = async (userId: string): Promise<SyncPlan> => {
  const localMaps = getMapIndex();
  const supabaseMaps = await pullMaps(userId);

  const plan: SyncPlan = { toUpload: [], toDownload: [], conflicts: [], toDeleteLocally: [] };
  const localBySuppId: Record<string, MapMeta> = {};
  localMaps.forEach(m => { if (m.supabase_id) localBySuppId[m.supabase_id] = m; });

  const localSupabaseIds = localMaps.filter(m => m.supabase_id).map(m => m.supabase_id!);
  if (localSupabaseIds.length > 0) {
    try {
      const { data: deletedOnCloud } = await supabase
        .from('maps')
        .select('id')
        .in('id', localSupabaseIds)
        .not('deleted_at', 'is', null);

      const deletedIds = new Set((deletedOnCloud || []).map(m => m.id));
      localMaps.forEach(m => {
        if (m.supabase_id && deletedIds.has(m.supabase_id)) {
          plan.toDeleteLocally.push(m);
        }
      });
    } catch (err) {
      console.warn('[Sync] Could not check for deleted maps on cloud:', err);
    }
  }

  const toDeleteLocallyIds = new Set(plan.toDeleteLocally.map(m => m.id));

  localMaps.forEach(m => {
    if (!m.supabase_id && !toDeleteLocallyIds.has(m.id)) {
      plan.toUpload.push(m);
    }
  });

  supabaseMaps.filter(isValidSupabaseMap).forEach(sm => {
    const local = localBySuppId[sm.id];
    if (!local) {
      plan.toDownload.push({ ...sm, nodeCount: (sm.data?.nodes || []).length });
    } else if (!toDeleteLocallyIds.has(local.id)) {
      const localTime = new Date(local.updatedAt).getTime();
      const suppTime = new Date(sm.updated_at).getTime();
      const DIFF_THRESHOLD = 2000;

      if (localTime > suppTime + DIFF_THRESHOLD) {
        plan.toUpload.push(local);
      } else if (suppTime > localTime + DIFF_THRESHOLD) {
        plan.conflicts.push({
          local: { ...local, nodeCount: (loadMap(local.id)?.nodes || []).length },
          supabase: { ...sm, nodeCount: (sm.data?.nodes || []).length },
        });
      }
    }
  });

  console.log('[Sync] Plan built:', {
    upload: plan.toUpload.length,
    download: plan.toDownload.length,
    conflicts: plan.conflicts.length,
    deleteLocal: plan.toDeleteLocally.length
  });

  return plan;
};

interface SyncResolution {
  conflict: { local: any; supabase: any };
  choice: 'local' | 'cloud';
}

export const executeSyncPlan = async (plan: SyncPlan, resolutions: SyncResolution[], userId: string): Promise<void> => {
  for (const meta of plan.toUpload) {
    await pushMap(meta.id, userId);
  }

  for (const sm of plan.toDownload.filter(isValidSupabaseMap)) {
    const newMeta = createMap(sm.name);
    saveLocalMap(newMeta.id, sm.data);
    setMapIndex(getMapIndex().map(m =>
      m.id === newMeta.id
        ? { ...m, supabase_id: sm.id, createdAt: sm.created_at, updatedAt: sm.updated_at }
        : m
    ));
  }

  for (const { choice, conflict } of resolutions) {
    if (choice === 'local') {
      await pushMap(conflict.local.id, userId);
    } else {
      saveLocalMap(conflict.local.id, conflict.supabase.data);
      setMapIndex(getMapIndex().map(m =>
        m.id === conflict.local.id
          ? { ...m, updatedAt: conflict.supabase.updated_at }
          : m
      ));
    }
  }

  for (const meta of (plan.toDeleteLocally || [])) {
    deleteLocalMap(meta.id);
  }
};
